import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { BusFront } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Modal } from '@/components/common/Modal';
import { SelectField, TextField } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect, ListPage } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useCampusPicker } from '@/hooks/usePickers';
import { useTableQuery } from '@/hooks/useTableQuery';
import { vehiclesApi } from '@/services/api/endpoints/transport';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import { ROUTES } from '@/app/router/routes';
import type { FuelType, Vehicle, VehicleInput, VehicleStatus, VehicleType } from '@/types/transport';
import { humanise } from '@/utils/format';
import { ComplianceBadge, VehicleStatusBadge } from './shared';

const VEHICLE_STATUSES: VehicleStatus[] = [
  'ACTIVE',
  'INACTIVE',
  'UNDER_MAINTENANCE',
  'OUT_OF_SERVICE',
  'RETIRED',
];

const VEHICLE_TYPES: VehicleType[] = ['BUS', 'MINIBUS', 'COASTER', 'VAN', 'CAR', 'OTHER'];

const FUEL_TYPES: FuelType[] = ['DIESEL', 'PETROL', 'CNG', 'ELECTRIC', 'HYBRID', 'OTHER'];

/**
 * The fleet.
 *
 * <p>Searching and filtering happen on the server; the page shows one page of
 * vehicles, never the whole garage. The search box matches plate, fleet number,
 * make and model.
 *
 * <p>The papers column is the server's verdict on each vehicle's documents —
 * the worst state among its dated papers, derived against the school's warning
 * window. Nothing here re-derives it (§38).
 *
 * <p>A vehicle is retired, never deleted: its trips and maintenance history
 * point at it for as long as the school keeps records. Retiring therefore asks
 * for a reason, and the server refuses the change without one.
 */
export function VehiclesPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const table = useTableQuery('registrationNumber,asc');
  const manages = can(PERMISSIONS.VEHICLE_MANAGE);

  const [status, setStatusFilter] = useState<VehicleStatus | ''>('');
  const [campusFilter, setCampusFilter] = useState('');

  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Vehicle | null>(null);
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [fleetNumber, setFleetNumber] = useState('');
  const [vehicleType, setVehicleType] = useState<VehicleType | ''>('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [modelYear, setModelYear] = useState('');
  const [color, setColor] = useState('');
  const [seatingCapacity, setSeatingCapacity] = useState('');
  const [mileage, setMileage] = useState('');
  const [fuelType, setFuelType] = useState<FuelType | ''>('');
  const [campusId, setCampusId] = useState('');
  const [acquiredOn, setAcquiredOn] = useState('');
  const [notes, setNotes] = useState('');

  const [changing, setChanging] = useState<Vehicle | null>(null);
  const [nextStatus, setNextStatus] = useState<VehicleStatus>('ACTIVE');
  const [statusReason, setStatusReason] = useState('');

  const campuses = useCampusPicker();

  const params = {
    ...table.params,
    ...(status === '' ? {} : { status }),
    ...(campusFilter === '' ? {} : { campusId: campusFilter }),
  };

  const vehicles = useQuery({
    queryKey: queryKeys.vehicles.list(params),
    queryFn: () => vehiclesApi.list(params),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.vehicles.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.transport.all });
  };

  function open(vehicle: Vehicle | null) {
    setEditing(vehicle);
    setAdding(vehicle === null);
    setRegistrationNumber(vehicle?.registrationNumber ?? '');
    setFleetNumber(vehicle?.fleetNumber ?? '');
    setVehicleType(vehicle?.vehicleType ?? '');
    setMake(vehicle?.make ?? '');
    setModel(vehicle?.model ?? '');
    setModelYear(vehicle?.modelYear === undefined ? '' : String(vehicle.modelYear));
    setColor(vehicle?.color ?? '');
    setSeatingCapacity(vehicle === null ? '' : String(vehicle.seatingCapacity));
    setMileage(vehicle === null ? '' : String(vehicle.currentMileageKm));
    setFuelType(vehicle?.fuelType ?? '');
    setCampusId(vehicle?.campusId ?? '');
    setAcquiredOn(vehicle?.acquiredOn ?? '');
    setNotes(vehicle?.notes ?? '');
  }

  function closeForm() {
    setAdding(false);
    setEditing(null);
  }

  const save = useMutation({
    mutationFn: () => {
      const payload: VehicleInput = {
        registrationNumber: registrationNumber.trim(),
        fleetNumber: fleetNumber.trim() === '' ? undefined : fleetNumber.trim(),
        vehicleType: vehicleType === '' ? undefined : vehicleType,
        make: make.trim() === '' ? undefined : make.trim(),
        model: model.trim() === '' ? undefined : model.trim(),
        modelYear: modelYear.trim() === '' ? undefined : Number(modelYear),
        color: color.trim() === '' ? undefined : color.trim(),
        seatingCapacity: Number(seatingCapacity),
        currentMileageKm: mileage.trim() === '' ? undefined : Number(mileage),
        fuelType: fuelType === '' ? undefined : fuelType,
        campusId: campusId === '' ? null : campusId,
        acquiredOn: acquiredOn === '' ? undefined : acquiredOn,
        notes: notes.trim() === '' ? undefined : notes.trim(),
      };
      return editing === null ? vehiclesApi.create(payload) : vehiclesApi.update(editing.id, payload);
    },
    onSuccess: (vehicle) => {
      toast.success(`${vehicle.displayName} saved`);
      closeForm();
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const setStatus = useMutation({
    mutationFn: (vehicle: Vehicle) =>
      vehiclesApi.setStatus(
        vehicle.id,
        nextStatus,
        statusReason.trim() === '' ? undefined : statusReason.trim(),
      ),
    onSuccess: (vehicle) => {
      toast.success(`${vehicle.displayName} is now ${humanise(vehicle.status).toLowerCase()}`);
      setChanging(null);
      setStatusReason('');
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const columns: Column<Vehicle>[] = [
    {
      key: 'vehicle',
      header: 'Vehicle',
      sortKey: 'registrationNumber',
      render: (row) => (
        <span>
          <Link
            to={ROUTES.vehicleDetail(row.id)}
            className="block text-sm font-semibold text-ink underline-offset-2 hover:text-accent hover:underline"
          >
            {row.displayName}
          </Link>
          <span className="block font-mono text-[11px] text-subtle">{row.registrationNumber}</span>
        </span>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      sortKey: 'vehicleType',
      render: (row) => (
        <span>
          <span className="block text-sm text-ink">{humanise(row.vehicleType)}</span>
          <span className="block text-[11px] text-subtle">
            {row.make === undefined && row.model === undefined
              ? '—'
              : [row.make, row.model].filter((part) => part !== undefined).join(' ')}
          </span>
        </span>
      ),
    },
    {
      key: 'seats',
      header: 'Seats',
      sortKey: 'seatingCapacity',
      className: 'text-right',
      render: (row) => <span className="tabular-nums">{row.seatingCapacity}</span>,
    },
    {
      key: 'mileage',
      header: 'Odometer',
      sortKey: 'currentMileageKm',
      className: 'text-right',
      render: (row) => (
        <span className="tabular-nums">{row.currentMileageKm.toLocaleString()} km</span>
      ),
    },
    {
      key: 'campus',
      header: 'Campus',
      render: (row) => row.campusName ?? 'Any campus',
    },
    {
      key: 'papers',
      header: 'Papers',
      render: (row) => <ComplianceBadge state={row.documentCompliance} />,
    },
    {
      key: 'status',
      header: 'Status',
      sortKey: 'status',
      render: (row) => <VehicleStatusBadge status={row.status} />,
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (row) =>
        manages ? (
          <span className="flex justify-end gap-1.5">
            <Button size="sm" variant="secondary" onClick={() => open(row)}>
              Edit
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setChanging(row);
                setNextStatus(row.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE');
                setStatusReason('');
              }}
            >
              Status
            </Button>
          </span>
        ) : null,
    },
  ];

  return (
    <>
      <ListPage
        title="Vehicles"
        description="The fleet, with each vehicle's papers as the server judges them."
        icon={<BusFront className="h-5 w-5" aria-hidden="true" />}
        action={manages ? <Button onClick={() => open(null)}>Register a vehicle</Button> : undefined}
        search={{
          value: table.searchInput,
          onChange: table.setSearchInput,
          placeholder: 'Plate, fleet number, make, model…',
        }}
        filters={
          <>
            <FilterSelect
              label="Status"
              value={status}
              onChange={(value) => {
                setStatusFilter(value);
                table.setPage(0);
              }}
              options={VEHICLE_STATUSES.map((value) => ({ value, label: humanise(value) }))}
            />
            <FilterSelect
              label="Campus"
              value={campusFilter}
              onChange={(value) => {
                setCampusFilter(value);
                table.setPage(0);
              }}
              options={(campuses.data ?? []).map((row) => ({ value: row.id, label: row.name }))}
            />
          </>
        }
        pagination={vehicles.data?.pagination}
        onPageChange={table.setPage}
        onSizeChange={table.setSize}
      >
        <DataTable
          columns={columns}
          rows={vehicles.data?.data}
          rowKey={(row) => row.id}
          loading={vehicles.isPending}
          error={vehicles.error}
          onRetry={() => void vehicles.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          emptyTitle={table.searchInput === '' ? 'No vehicles yet' : 'Nothing matched'}
          emptyDescription={
            table.searchInput === ''
              ? 'Register the first vehicle to start the fleet.'
              : 'The search matches plate, fleet number, make and model.'
          }
        />
      </ListPage>

      <Modal
        open={adding || editing !== null}
        onClose={closeForm}
        title={editing === null ? 'Register a vehicle' : editing.displayName}
        description="Registration numbers are unique per school. A vehicle is retired, never deleted."
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={closeForm}>
              Cancel
            </Button>
            <Button
              onClick={() => save.mutate()}
              loading={save.isPending}
              disabled={registrationNumber.trim() === '' || seatingCapacity.trim() === ''}
            >
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <TextField
              label="Registration number"
              value={registrationNumber}
              onChange={(event) => setRegistrationNumber(event.target.value)}
              required
            />
            <TextField
              label="Fleet number"
              value={fleetNumber}
              onChange={(event) => setFleetNumber(event.target.value)}
              hint='The school&apos;s own shorthand — "Bus 7". Unique when present.'
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <SelectField
              label="Type"
              value={vehicleType}
              onChange={(event) => setVehicleType(event.target.value as VehicleType | '')}
            >
              <option value="">Not stated</option>
              {VEHICLE_TYPES.map((value) => (
                <option key={value} value={value}>
                  {humanise(value)}
                </option>
              ))}
            </SelectField>
            <SelectField
              label="Fuel"
              value={fuelType}
              onChange={(event) => setFuelType(event.target.value as FuelType | '')}
            >
              <option value="">Not stated</option>
              {FUEL_TYPES.map((value) => (
                <option key={value} value={value}>
                  {humanise(value)}
                </option>
              ))}
            </SelectField>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <TextField label="Make" value={make} onChange={(event) => setMake(event.target.value)} />
            <TextField label="Model" value={model} onChange={(event) => setModel(event.target.value)} />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <TextField
              label="Model year"
              type="number"
              value={modelYear}
              onChange={(event) => setModelYear(event.target.value)}
            />
            <TextField label="Colour" value={color} onChange={(event) => setColor(event.target.value)} />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <TextField
              label="Seats"
              type="number"
              min={1}
              value={seatingCapacity}
              onChange={(event) => setSeatingCapacity(event.target.value)}
              required
            />
            <TextField
              label="Odometer (km)"
              type="number"
              min={0}
              value={mileage}
              onChange={(event) => setMileage(event.target.value)}
              hint="Only rolls forward — a smaller reading is refused, never silently taken."
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <SelectField
              label="Campus"
              value={campusId}
              onChange={(event) => setCampusId(event.target.value)}
            >
              <option value="">Any campus</option>
              {(campuses.data ?? []).map((row) => (
                <option key={row.id} value={row.id}>
                  {row.name}
                </option>
              ))}
            </SelectField>
            <TextField
              label="Acquired on"
              type="date"
              value={acquiredOn}
              onChange={(event) => setAcquiredOn(event.target.value)}
            />
          </div>
          <TextField label="Notes" value={notes} onChange={(event) => setNotes(event.target.value)} />
        </div>
      </Modal>

      <Modal
        open={changing !== null}
        onClose={() => setChanging(null)}
        title={changing === null ? '' : `Change ${changing.displayName}'s status`}
        description="Only an active vehicle can be put on a trip. Retiring is final; the history stays."
        footer={
          <>
            <Button variant="secondary" onClick={() => setChanging(null)}>
              Cancel
            </Button>
            <Button
              variant={nextStatus === 'RETIRED' ? 'danger' : 'primary'}
              loading={setStatus.isPending}
              disabled={nextStatus === 'RETIRED' && statusReason.trim() === ''}
              onClick={() => {
                if (changing !== null) {
                  setStatus.mutate(changing);
                }
              }}
            >
              {nextStatus === 'RETIRED' ? 'Retire it' : 'Change status'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <SelectField
            label="New status"
            value={nextStatus}
            onChange={(event) => setNextStatus(event.target.value as VehicleStatus)}
          >
            {VEHICLE_STATUSES.filter((value) => value !== changing?.status).map((value) => (
              <option key={value} value={value}>
                {humanise(value)}
              </option>
            ))}
          </SelectField>
          <TextField
            label="Reason"
            value={statusReason}
            onChange={(event) => setStatusReason(event.target.value)}
            required={nextStatus === 'RETIRED'}
            hint="Required when retiring. Kept on the vehicle's notes either way."
          />
        </div>
      </Modal>
    </>
  );
}
